// app/components/BuySell.tsx
'use client'

import React, { useState, useEffect } from 'react'
import styles from './BuySell.module.css'
import { usePathname } from 'next/navigation'

interface BuySellProps {
  symbol?: string
  initialPrice?: string
  initialAction?: 'BUY' | 'SELL' | null
}

const TABS = ['Market', 'Limit']

export default function BuySell({ symbol: propSymbol, initialPrice = '', initialAction = null }: BuySellProps) {
  const pathname = usePathname()
  // Extract the first segment after the domain from the URL path
  const pathSegments = pathname ? pathname.split('/').filter(Boolean) : []
  const symbolFromPath = pathSegments[0] || 'btcusdt'

  // If the symbol ends with .NS or .BS, use a fallback symbol
  const defaultSymbol =
    symbolFromPath.toUpperCase().endsWith('.NS') ||
    symbolFromPath.toUpperCase().endsWith('.BS')
      ? 'btcusdt'
      : symbolFromPath

  // State for symbol selection and custom input
  const [currentSymbol, setCurrentSymbol] = useState(propSymbol || defaultSymbol)
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false)
  const [symbolInput, setSymbolInput] = useState('')
  const [popularSymbols] = useState([
    'BTCUSDT',
    'ETHUSDT',
    'BNBUSDT',
    'SOLUSDT',
    'LTCUSDT'
  ])
  
  const [activeTab, setActiveTab] = useState<string>(initialPrice ? 'Limit' : 'Market')
  const [price, setPrice] = useState(initialPrice)
  const [amount, setAmount] = useState('')

  const handleSymbolChange = (newSymbol: string) => {
    const formattedSymbol = newSymbol.toUpperCase()
    setCurrentSymbol(formattedSymbol)
    setIsSymbolPopupOpen(false)
    setSymbolInput('')
  }

  const handleBuy = () => {
    alert(`BUY ${amount} of ${currentSymbol} at ${activeTab} price ${price || 'MKT'}`)
  }
  
  // Auto-trigger buy/sell if initialAction is provided
  // useEffect(() => {
  //   if (initialAction === 'BUY' && price) {
  //     handleBuy();
  //   } else if (initialAction === 'SELL' && price) {
  //     handleSell();
  //   }
  // }, [initialAction, price]);

  const handleSell = () => {
    alert(`SELL ${amount} of ${currentSymbol} at ${activeTab} price ${price || 'MKT'}`)
  }

  return (
    <div className={styles.container}>
      {/* Header with Title and Symbol Selector */}
      <div className={styles.header}>
        <span className={styles.title}>Trade</span>
        <div className={styles.controls}>
          {/* Symbol Selector */}
          <div className={styles.symbolSelector}>
            <select
              value={currentSymbol}
              onChange={e => {
                if (e.target.value === 'custom') {
                  setIsSymbolPopupOpen(true)
                } else {
                  handleSymbolChange(e.target.value)
                }
              }}
              aria-label="Select Symbol"
              className={styles.symbolSelect}
            >
              <option value={currentSymbol}>
                {currentSymbol.toUpperCase()}
              </option>
              {popularSymbols.map(
                symbol =>
                  symbol !== currentSymbol.toUpperCase() && (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  )
              )}
              <option value="custom">Custom</option>
            </select>
            {isSymbolPopupOpen && (
              <div className={styles.customSymbolPopup}>
                <input
                  type="text"
                  placeholder="Enter symbol (e.g. BTCUSDT)"
                  value={symbolInput}
                  onChange={e => setSymbolInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && symbolInput.trim()) {
                      handleSymbolChange(symbolInput.trim())
                    }
                  }}
                  className={styles.customSymbolInput}
                />
                <div className={styles.customSymbolButtons}>
                  <button
                    onClick={() => setIsSymbolPopupOpen(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (symbolInput.trim()) {
                        handleSymbolChange(symbolInput.trim())
                      }
                    }}
                    className={styles.applyButton}
                    disabled={!symbolInput.trim()}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Tab Header */}
      <div className={styles.tabHeader}>
        {TABS.map(t => (
          <div
            key={t}
            className={`${styles.tab} ${activeTab === t ? styles.activeTab : ''}`}
            onClick={() => {
              setActiveTab(t)
              if (t === 'Market') setPrice('')
            }}
          >
            {t}
          </div>
        ))}
      </div>
      <div className={styles.tabContent}>
        {activeTab === 'Limit' && (
          <div className={styles.formRow}>
            <label>Price:</label>
            <input
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="Enter limit price"
            />
          </div>
        )}
        <div className={styles.formRow}>
          <label>Amount:</label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Enter amount"
          />
        </div>
        <div className={styles.buttons}>
          <button className={styles.buyButton} onClick={handleBuy}>
            Buy
          </button>
          <button className={styles.sellButton} onClick={handleSell}>
            Sell
          </button>
        </div>
      </div>
    </div>
  )
}
